import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useLanguageLearning } from '../contexts/LanguageLearningContext';
import { getLearningLanguage, getLearningLanguageTranslationKey } from '../constants/learningLanguages';

const SEARCH_THRESHOLD = 6; // Show the search input once the list grows to 6+ languages.

const MenuLearningLanguagePicker = ({ onAfterSelect }) => {
  const { activeLanguage, availableLanguages, switchLanguage, getLanguageInfo } = useLanguageLearning();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const instanceIdRef = useRef(`menu-lang-picker-${Math.random().toString(36).slice(2, 10)}`);
  const paletteId = `${instanceIdRef.current}-palette`;
  const listboxId = `${instanceIdRef.current}-listbox`;

  const resolveLanguageMeta = useCallback(
    (code) => {
      const info = getLanguageInfo(code) || getLearningLanguage(code);
      const translationKey = getLearningLanguageTranslationKey(code);
      const displayName = translationKey
        ? t(`languageNames.${translationKey}`, info?.name || getLearningLanguage(code).name)
        : (info?.name || getLearningLanguage(code).name);
      return {
        code,
        displayName,
        nativeName: info?.nativeName || getLearningLanguage(code).nativeName || displayName,
        flagPath: info?.flagPath || getLearningLanguage(code).flagPath || null,
      };
    },
    [getLanguageInfo, t]
  );

  const languages = useMemo(
    () => availableLanguages.map((lang) => resolveLanguageMeta(lang.code)),
    [availableLanguages, resolveLanguageMeta]
  );

  const currentLanguage = useMemo(
    () => languages.find((lang) => lang.code === activeLanguage) || languages[0],
    [languages, activeLanguage]
  );

  const filteredLanguages = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return languages;
    return languages.filter((lang) => (
      lang.displayName.toLowerCase().includes(normalizedQuery)
      || lang.nativeName.toLowerCase().includes(normalizedQuery)
      || lang.code.toLowerCase().includes(normalizedQuery)
    ));
  }, [languages, query]);

  const showSearch = languages.length >= SEARCH_THRESHOLD;

  // Reset highlight when filtered list changes
  useEffect(() => {
    if (!isOpen) return;
    if (highlightIndex >= filteredLanguages.length) {
      setHighlightIndex(0);
    }
  }, [filteredLanguages.length, highlightIndex, isOpen]);

  // Click outside closes the palette
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Focus the search (or listbox) when the palette opens
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => {
      if (showSearch && searchInputRef.current) {
        searchInputRef.current.focus();
      } else if (listRef.current) {
        listRef.current.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, showSearch]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const activeEl = listRef.current.querySelector('[data-highlighted="true"]');
    if (activeEl && typeof activeEl.scrollIntoView === 'function') {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, isOpen, filteredLanguages]);

  // Byte-equivalent to the previous picker's handleSelect.
  // The palette unmounts naturally when the Header closes the menu drawer via onAfterSelect.
  const handleSelect = useCallback(
    async (code) => {
      if (!code || code === activeLanguage) {
        if (onAfterSelect) onAfterSelect();
        return;
      }
      try {
        await switchLanguage(code);
      } catch {
        void 0;
      }
      if (onAfterSelect) onAfterSelect();
      navigate(`/${code}/podcast-lessons`, { replace: true });
    },
    [activeLanguage, navigate, onAfterSelect, switchLanguage]
  );

  const closePaletteAndReturnFocus = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setHighlightIndex(0);
    if (triggerRef.current) triggerRef.current.focus();
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closePaletteAndReturnFocus();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (filteredLanguages.length === 0) return;
        setHighlightIndex((prev) => (prev + 1) % filteredLanguages.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (filteredLanguages.length === 0) return;
        setHighlightIndex((prev) => (prev - 1 + filteredLanguages.length) % filteredLanguages.length);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        setHighlightIndex(0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        setHighlightIndex(Math.max(0, filteredLanguages.length - 1));
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const target = filteredLanguages[highlightIndex];
        if (target) handleSelect(target.code);
      }
    },
    [closePaletteAndReturnFocus, filteredLanguages, handleSelect, highlightIndex, isOpen]
  );

  const triggerLabel = t(
    'common:navigation.menuItems.learningLanguage.triggerAria',
    { language: currentLanguage?.displayName || '' }
  );

  const paletteMotion = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.15 },
      }
    : {
        initial: { opacity: 0, y: -6, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -4, scale: 0.98 },
        transition: { type: 'spring', stiffness: 340, damping: 26, mass: 0.8 },
      };

  return (
    <div ref={wrapperRef} className="relative w-full" onKeyDown={handleKeyDown}>
      {/* Trigger: identity statement */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={paletteId}
        aria-label={triggerLabel}
        onClick={() => setIsOpen((open) => !open)}
        className="group flex w-full flex-col items-start gap-1.5 rounded-lg px-1 py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9d1c7f] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FDFBF7]"
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500">
          {t('common:navigation.menuItems.learningLanguage.heading', "I'm learning")}
        </span>
        <span className="flex items-baseline gap-2">
          <span className="relative inline-block pb-2">
            <span
              className="font-display text-3xl font-semibold leading-none text-stone-900 transition-colors group-hover:text-[#9d1c7f]"
              style={{ letterSpacing: '-0.02em' }}
            >
              {currentLanguage?.displayName || ''}
            </span>
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute bottom-0 left-1/2 w-[85%] -translate-x-1/2"
              height="10"
              viewBox="0 0 300 12"
              preserveAspectRatio="none"
              fill="none"
            >
              <path
                d="M5 7 Q 150 2, 295 7"
                stroke="#B2288E"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-stone-400 transition-all duration-200 group-hover:text-[#9d1c7f] ${isOpen ? 'rotate-180' : ''}`}
            strokeWidth={2}
          />
        </span>
      </button>

      {/* Expanded command palette */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={paletteId}
            role="dialog"
            aria-label={t('common:navigation.menuItems.learningLanguage.paletteAria', 'Choose a learning language')}
            {...paletteMotion}
            className="absolute left-0 right-0 z-30 mt-3 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_20px_40px_-16px_rgba(60,30,45,0.18)]"
          >
            {showSearch && (
              <div className="flex items-center gap-3 border-b border-stone-100 px-4 py-3">
                <Search className="h-4 w-4 shrink-0 text-stone-400" strokeWidth={2} aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlightIndex(0);
                  }}
                  placeholder={t('common:navigation.menuItems.learningLanguage.searchPlaceholder', 'Search languages')}
                  aria-label={t('common:navigation.menuItems.learningLanguage.searchPlaceholder', 'Search languages')}
                  aria-controls={listboxId}
                  aria-activedescendant={
                    filteredLanguages[highlightIndex]
                      ? `${listboxId}-option-${filteredLanguages[highlightIndex].code}`
                      : undefined
                  }
                  className="flex-1 border-0 bg-transparent text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none"
                />
                <kbd className="hidden rounded border border-stone-200 px-1.5 py-0.5 text-[10px] font-semibold text-stone-400 sm:inline">
                  ESC
                </kbd>
              </div>
            )}

            <ul
              id={listboxId}
              ref={listRef}
              role="listbox"
              aria-label={t('common:navigation.menuItems.learningLanguage.paletteAria', 'Choose a learning language')}
              tabIndex={-1}
              className="max-h-[320px] overflow-y-auto py-1 focus:outline-none"
            >
              {filteredLanguages.length === 0 ? (
                <li className="px-4 py-8 text-center text-xs text-stone-500">
                  {t('common:navigation.menuItems.learningLanguage.noResults', 'No languages match your search.')}
                </li>
              ) : (
                filteredLanguages.map((lang, idx) => {
                  const isActive = lang.code === activeLanguage;
                  const isHighlighted = idx === highlightIndex;
                  return (
                    <li key={lang.code}>
                      <button
                        id={`${listboxId}-option-${lang.code}`}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        data-highlighted={isHighlighted}
                        onClick={() => handleSelect(lang.code)}
                        onMouseEnter={() => setHighlightIndex(idx)}
                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                          isHighlighted ? 'bg-stone-50' : ''
                        } ${isActive ? 'font-semibold text-stone-900' : 'text-stone-600 hover:text-stone-900'}`}
                      >
                        {lang.flagPath && (
                          <img
                            src={lang.flagPath}
                            alt=""
                            aria-hidden="true"
                            className="h-[14px] w-5 shrink-0 rounded-sm border border-stone-200/50 object-cover"
                            loading="lazy"
                          />
                        )}
                        <span className="flex flex-1 items-baseline gap-2 min-w-0">
                          <span className="truncate">{lang.displayName}</span>
                          {lang.nativeName && lang.nativeName !== lang.displayName && (
                            <span className="truncate text-xs font-normal text-stone-400">
                              {lang.nativeName}
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                          {lang.code}
                        </span>
                        {isActive && (
                          <Check className="h-4 w-4 shrink-0 text-[#9d1c7f]" strokeWidth={2.5} aria-hidden="true" />
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            {showSearch && filteredLanguages.length > 0 && (
              <div className="flex items-center justify-between border-t border-stone-100 px-4 py-2 text-[10px] text-stone-400">
                <div className="hidden items-center gap-3 sm:flex">
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-stone-200 px-1 py-0.5 font-semibold text-stone-500">↑↓</kbd>
                    {t('common:navigation.menuItems.learningLanguage.keyboardNavigate', 'navigate')}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded border border-stone-200 px-1 py-0.5 font-semibold text-stone-500">↵</kbd>
                    {t('common:navigation.menuItems.learningLanguage.keyboardSelect', 'select')}
                  </span>
                </div>
                <span className="ml-auto font-semibold text-stone-500">
                  {query.trim()
                    ? t('common:navigation.menuItems.learningLanguage.countFiltered', {
                        filtered: filteredLanguages.length,
                        total: languages.length,
                        defaultValue: `${filteredLanguages.length} of ${languages.length}`,
                      })
                    : t('common:navigation.menuItems.learningLanguage.countLabel', {
                        count: languages.length,
                        defaultValue: `${languages.length} languages`,
                      })}
                </span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuLearningLanguagePicker;
